import { useNode } from "@craftjs/core";
import React from "react";
import { isUpdatedFromDefault } from "../../utils";

import {
  ColorInput,
  UnitInput,
  ToggleInput,
  BoxShadowInput,
  PaddingInput,
  Panel,
  Tabs,
  TextInput,
  BorderRadiusInput,
  BorderInput,
  IconInput,
} from "../../input";
import { supportLibrary } from "../../block-supports";
import { INPUT_LIMITS } from "../../input-limits";

export const SearchSettings = () => {
  const {
    id,
    actions: { setProp },
    props,
    dom,
    supports,
  } = useNode((node) => ({
    id: node.id,
    text: node.data.props.text,
    props: node.data.props,
    dom: node.dom,
    supports: node.data?.type?.craft?.related?.supports,
  }));

  const {
    containerWidth,
    containerWidthTablet,
    containerWidthMobile,
    containerHeight,
    containerHeightTablet,
    containerHeightMobile,
    containerMaxWidth = "100%",
    containerMaxHeight,
    gap,
    gapTablet,
    gapMobile,
    placeholderText = "Search...",
    placeholderSize,
    inputWidth,
    inputWidthTablet,
    inputWidthMobile,
    inputHeight,
    inputHeightTablet,
    inputHeightMobile,
    inputMaxWidth,
    inputMaxHeight,
    isSearchButtonVisible = true,
    isSearchButtonIcon = true,
    searchButtonText = "Search",
    icon = "search",
    iconSize = "20px",
    iconSizeTablet,
    iconSizeMobile,
    hasCustomIcon,
    customIcon,
    containerBackground,
    inputColor,
    inputPlaceholderColor,
    inputBackgroundColor,
    searchColor = "#ffffff",
    searchBackground = "#745dcd",
    containerPadding,
    containerPaddingTablet,
    containerPaddingMobile,
    containerBorder = {
      top: {
        width: "1px",
        style: "solid",
        color: "#666666",
      },
      left: {
        width: "1px",
        style: "solid",
        color: "#666666",
      },
      right: {
        width: "1px",
        style: "solid",
        color: "#666666",
      },
      bottom: {
        width: "1px",
        style: "solid",
        color: "#666666",
      },
    },
    containerBorderRadius,
    containerBoxShadow,
    inputPadding,
    inputPaddingTablet,
    inputPaddingMobile,
    inputBorder,
    inputBorderRadius,
    inputBoxShadow,
    searchButtonPadding = {
      top: "15px",
      left: "15px",
      right: "15px",
      bottom: "15px",
    },
    searchButtonPaddingTablet,
    searchButtonPaddingMobile,
    searchButtonBorder,
    searchButtonBorderRadius,
    searchButtonBoxShadow,
  } = props;

  function GeneralSettings() {
    return (
      <div className="g-settings__wrapper">
        <Panel
          isUpdated={isUpdatedFromDefault([
            { key: containerWidth },
            { key: containerWidthMobile },
            { key: containerWidthTablet },
            { key: containerHeight },
            { key: containerHeightMobile },
            { key: containerHeightTablet },
            { key: containerMaxWidth, default: "100%" },
            { key: containerMaxHeight },
            { key: gapMobile },
            { key: gapTablet },
            { key: gap },
          ])}
          id={`block-setting-${id}-container`}
          title={"Container"}
          initialOpen={true}
        >
          <UnitInput
            isResponsive={true}
            resetValue={undefined}
            value={containerWidth}
            valueMobile={containerWidthMobile}
            valueTablet={containerWidthTablet}
            onChange={(val) => {
              setProp((props) => {
                props.containerWidth = val;
              });
            }}
            setValueTablet={(val) => {
              setProp((props) => {
                props.containerWidthTablet = val;
              });
            }}
            setValueMobile={(val) => {
              setProp((props) => {
                props.containerWidthMobile = val;
              });
            }}
            label={"Width"}
          />
          <UnitInput
            isResponsive={true}
            resetValue={undefined}
            value={containerHeight}
            valueMobile={containerHeightMobile}
            valueTablet={containerHeightTablet}
            onChange={(val) => {
              setProp((props) => {
                props.containerHeight = val;
              });
            }}
            setValueTablet={(val) => {
              setProp((props) => {
                props.containerHeightTablet = val;
              });
            }}
            setValueMobile={(val) => {
              setProp((props) => {
                props.containerHeightMobile = val;
              });
            }}
            label={"Height"}
          />
          <UnitInput
            resetValue={"100%"}
            value={containerMaxWidth}
            onChange={(val) => {
              setProp((props) => {
                props.containerMaxWidth = val;
              });
            }}
            label={"Max Width"}
          />
          <UnitInput
            resetValue={undefined}
            value={containerMaxHeight}
            onChange={(val) => {
              setProp((props) => {
                props.containerMaxHeight = val;
              });
            }}
            label={"Max Height"}
          />
          <UnitInput
            isResponsive={true}
            resetValue={undefined}
            valueMobile={gapMobile}
            valueTablet={gapTablet}
            value={gap}
            onChange={(val) => {
              setProp((props) => {
                props.gap = val;
              });
            }}
            setValueTablet={(val) => {
              setProp((props) => {
                props.gapTablet = val;
              });
            }}
            setValueMobile={(val) => {
              setProp((props) => {
                props.gapMobile = val;
              });
            }}
            label={"Gap"}
          />
        </Panel>
        <Panel
          isUpdated={isUpdatedFromDefault([
            { key: placeholderText, default: "Search..." },
            { key: inputWidth },
            { key: inputWidthMobile },
            { key: inputWidthTablet },
            { key: inputHeight },
            { key: inputHeightMobile },
            { key: inputHeightTablet },
            { key: inputMaxWidth },
            { key: inputMaxHeight },
            { key: placeholderSize },
          ])}
          id={`block-setting-${id}-input`}
          title={"Input"}
          initialOpen={false}
        >
          <TextInput
            minLength={INPUT_LIMITS["search-placeholder"].minlength}
            maxLength={INPUT_LIMITS["search-placeholder"].maxlength}
            value={placeholderText}
            onChange={(val) => {
              setProp((props) => {
                props.placeholderText = val;
              });
            }}
            label={"Placeholder"}
          />
          <UnitInput
            resetValue={undefined}
            value={placeholderSize}
            onChange={(val) => {
              setProp((props) => {
                props.placeholderSize = val;
              });
            }}
            label={"Size"}
          />
          <UnitInput
            isResponsive={true}
            resetValue={undefined}
            value={inputWidth}
            valueMobile={inputWidthMobile}
            valueTablet={inputWidthTablet}
            onChange={(val) => {
              setProp((props) => {
                props.inputWidth = val;
              });
            }}
            setValueTablet={(val) => {
              setProp((props) => {
                props.inputWidthTablet = val;
              });
            }}
            setValueMobile={(val) => {
              setProp((props) => {
                props.inputWidthMobile = val;
              });
            }}
            label={"Width"}
          />
          <UnitInput
            isResponsive={true}
            resetValue={undefined}
            value={inputHeight}
            valueMobile={inputHeightMobile}
            valueTablet={inputHeightTablet}
            onChange={(val) => {
              setProp((props) => {
                props.inputHeight = val;
              });
            }}
            setValueTablet={(val) => {
              setProp((props) => {
                props.inputHeightTablet = val;
              });
            }}
            setValueMobile={(val) => {
              setProp((props) => {
                props.inputHeightMobile = val;
              });
            }}
            label={"Height"}
          />
          <UnitInput
            resetValue={undefined}
            value={inputMaxWidth}
            onChange={(val) => {
              setProp((props) => {
                props.inputMaxWidth = val;
              });
            }}
            label={"Max Width"}
          />
          <UnitInput
            resetValue={undefined}
            value={inputMaxHeight}
            onChange={(val) => {
              setProp((props) => {
                props.inputMaxHeight = val;
              });
            }}
            label={"Max Height"}
          />
        </Panel>
        <Panel
          isUpdated={isUpdatedFromDefault([
            { key: isSearchButtonVisible, default: true },
            { key: isSearchButtonIcon, default: true },
            { key: iconSize, default: "20px" },
            { key: iconSizeMobile },
            { key: iconSizeTablet },
            { key: icon, default: "search" },
            { key: hasCustomIcon },
            { key: customIcon },
            { key: searchButtonText, default: "Search" },
          ])}
          id={`block-setting-${id}-search`}
          title={"Search"}
          initialOpen={false}
        >
          <ToggleInput
            value={isSearchButtonVisible}
            onChange={(val) => {
              setProp((props) => {
                props.isSearchButtonVisible = val;
              });
            }}
            label={"Search Button Visible"}
          />
          {isSearchButtonVisible && (
            <>
              <ToggleInput
                value={isSearchButtonIcon}
                onChange={(val) => {
                  setProp((props) => {
                    props.isSearchButtonIcon = val;
                  });
                }}
                label={"Search Button Icon"}
              />{" "}
              <UnitInput
                isResponsive={true}
                resetValue={"20px"}
                value={iconSize}
                valueMobile={iconSizeMobile}
                valueTablet={iconSizeTablet}
                onChange={(val) => {
                  setProp((props) => {
                    props.iconSize = val;
                  });
                }}
                setValueTablet={(val) => {
                  setProp((props) => {
                    props.iconSizeTablet = val;
                  });
                }}
                setValueMobile={(val) => {
                  setProp((props) => {
                    props.iconSizeMobile = val;
                  });
                }}
                label={"Size"}
              />
              {isSearchButtonIcon ? (
                <>
                  <IconInput
                    activeIcon={icon}
                    setActiveIcon={(val) => {
                      setProp((props) => {
                        props.icon = val;
                      });
                    }}
                    supportCustom
                    hasCustomIcon={hasCustomIcon}
                    setHasCustomIcon={(val) => {
                      setProp((props) => {
                        props.hasCustomIcon = val;
                      });
                    }}
                    customIcon={customIcon}
                    setCustomIcon={(val) => {
                      setProp((props) => {
                        props.customIcon = val;
                      });
                    }}
                  />
                </>
              ) : (
                <TextInput
                  minLength={INPUT_LIMITS["search-button"].minlength}
                  maxLength={INPUT_LIMITS["search-button"].maxlength}
                  value={searchButtonText}
                  onChange={(val) => {
                    setProp((props) => {
                      props.searchButtonText = val;
                    });
                  }}
                  label={"Search Button Text"}
                />
              )}
            </>
          )}
        </Panel>
      </div>
    );
  }

  function StyleSettings() {
    return (
      <>
        <div className="g-settings__wrapper">
          <Panel
            isUpdated={isUpdatedFromDefault([
              { key: inputColor },
              { key: containerBackground },
            ])}
            id={`block-setting-${id}-container`}
            title={"Container"}
            initialOpen={true}
          >
            <ColorInput
              value={inputColor}
              onChange={(val) => {
                setProp((props) => {
                  props.inputColor = val;
                });
              }}
              label={"Color"}
            />
            <ColorInput
              value={containerBackground}
              onChange={(val) => {
                setProp((props) => {
                  props.containerBackground = val;
                });
              }}
              label={"Background"}
            />
          </Panel>

          <Panel
            isUpdated={isUpdatedFromDefault([
              { key: inputPlaceholderColor },
              { key: inputBackgroundColor },
            ])}
            id={`block-setting-${id}-input`}
            title={"Input"}
          >
            <ColorInput
              value={inputPlaceholderColor}
              onChange={(val) => {
                setProp((props) => {
                  props.inputPlaceholderColor = val;
                });
              }}
              label={"Placeholder"}
            />
            <ColorInput
              value={inputBackgroundColor}
              onChange={(val) => {
                setProp((props) => {
                  props.inputBackgroundColor = val;
                });
              }}
              label={"Background"}
            />
          </Panel>
          <Panel
            isUpdated={isUpdatedFromDefault([
              { key: searchColor, default: "#ffffff" },
              { key: searchBackground, default: "#745dcd" },
            ])}
            id={`block-setting-${id}-search`}
            title={"Search"}
          >
            <ColorInput
              value={searchColor}
              resetValue={"#ffffff"}
              onChange={(val) => {
                setProp((props) => {
                  props.searchColor = val;
                });
              }}
              label={"Color"}
            />
            <ColorInput
              value={searchBackground}
              resetValue={"#745dcd"}
              onChange={(val) => {
                setProp((props) => {
                  props.searchBackground = val;
                });
              }}
              label={"Background"}
            />
          </Panel>
        </div>
      </>
    );
  }

  function AdvancedSettings() {
    return (
      <>
        <Panel
          isUpdated={isUpdatedFromDefault([
            { key: containerPaddingTablet },
            { key: containerPaddingMobile },
            { key: containerPadding },
          ])}
          id={`block-setting-${id}-container`}
          title={"Container Layout"}
          initialOpen={true}
        >
          <PaddingInput
            label={"Padding"}
            isResponsive={true}
            valueTablet={containerPaddingTablet}
            valueMobile={containerPaddingMobile}
            value={containerPadding}
            onChange={(val) => {
              setProp((props) => {
                props.containerPadding = val;
              });
            }}
            setValueTablet={(val) => {
              setProp((props) => {
                props.containerPaddingTablet = val;
              });
            }}
            setValueMobile={(val) => {
              setProp((props) => {
                props.containerPaddingMobile = val;
              });
            }}
          />
        </Panel>
        <Panel
          isUpdated={isUpdatedFromDefault([
            { key: inputPaddingTablet },
            { key: inputPaddingMobile },
            { key: inputPadding },
          ])}
          id={`block-setting-${id}-input-layout`}
          title={"Input Layout"}
          initialOpen={false}
        >
          <PaddingInput
            label={"Padding"}
            isResponsive={true}
            valueTablet={inputPaddingTablet}
            valueMobile={inputPaddingMobile}
            value={inputPadding}
            onChange={(val) => {
              setProp((props) => {
                props.inputPadding = val;
              });
            }}
            setValueTablet={(val) => {
              setProp((props) => {
                props.inputPaddingTablet = val;
              });
            }}
            setValueMobile={(val) => {
              setProp((props) => {
                props.inputPaddingMobile = val;
              });
            }}
          />
        </Panel>
        <Panel
          isUpdated={isUpdatedFromDefault([
            {
              key: containerBorder,
              default: {
                top: {
                  width: "1px",
                  style: "solid",
                  color: "#666666",
                },
                left: {
                  width: "1px",
                  style: "solid",
                  color: "#666666",
                },
                right: {
                  width: "1px",
                  style: "solid",
                  color: "#666666",
                },
                bottom: {
                  width: "1px",
                  style: "solid",
                  color: "#666666",
                },
              },
            },
            { key: containerBorderRadius },
          ])}
          id={`block-setting-${id}-border`}
          title={"Container Border"}
          initialOpen={true}
        >
          <BorderInput
            resetValue={{
              top: {
                width: "1px",
                style: "solid",
                color: "#666666",
              },
              left: {
                width: "1px",
                style: "solid",
                color: "#666666",
              },
              right: {
                width: "1px",
                style: "solid",
                color: "#666666",
              },
              bottom: {
                width: "1px",
                style: "solid",
                color: "#666666",
              },
            }}
            value={containerBorder}
            onChange={(val) => {
              setProp((props) => {
                props.containerBorder = val;
              });
            }}
            label={"Border"}
          />
          <BorderRadiusInput
            resetValue={undefined}
            value={containerBorderRadius}
            onChange={(val) => {
              setProp((props) => {
                props.containerBorderRadius = val;
              });
            }}
            label={"Border Radius"}
          />
        </Panel>
        <Panel
          isUpdated={isUpdatedFromDefault([
            { key: inputBorder },
            { key: inputBorderRadius },
          ])}
          id={`block-setting-${id}-input-border`}
          title={"Input Border"}
          initialOpen={false}
        >
          <BorderInput
            resetValue={undefined}
            value={inputBorder}
            onChange={(val) => {
              setProp((props) => {
                props.inputBorder = val;
              });
            }}
            label={"Border"}
          />
          <BorderRadiusInput
            resetValue={undefined}
            value={inputBorderRadius}
            onChange={(val) => {
              setProp((props) => {
                props.inputBorderRadius = val;
              });
            }}
            label={"Border Radius"}
          />
        </Panel>
        <Panel
          isUpdated={isUpdatedFromDefault([{ key: containerBoxShadow }])}
          id={`block-setting-${id}-border`}
          title={"Container Shadows"}
          initialOpen={true}
        >
          <BoxShadowInput
            resetValue={undefined}
            value={containerBoxShadow}
            onChange={(val) => {
              setProp((props) => {
                props.containerBoxShadow = val;
              });
            }}
          />
        </Panel>

        <Panel
          isUpdated={isUpdatedFromDefault([{ key: inputBoxShadow }])}
          id={`block-setting-${id}-input-shadows`}
          title={"Input Shadows"}
          initialOpen={false}
        >
          <BoxShadowInput
            resetValue={undefined}
            value={inputBoxShadow}
            onChange={(val) => {
              setProp((props) => {
                props.inputBoxShadow = val;
              });
            }}
          />
        </Panel>
        <Panel
          isUpdated={isUpdatedFromDefault([
            { key: searchButtonPaddingTablet },
            { key: searchButtonPaddingMobile },
            {
              key: searchButtonPadding,
              default: {
                top: "15px",
                left: "15px",
                right: "15px",
                bottom: "15px",
              },
            },
            { key: searchButtonBorder },
            { key: searchButtonBorderRadius },
            { key: searchButtonBoxShadow },
          ])}
          id={`block-setting-${id}-search-button`}
          title={"Search Button"}
          initialOpen={false}
        >
          <PaddingInput
            label={"Padding"}
            isResponsive={true}
            valueTablet={searchButtonPaddingTablet}
            valueMobile={searchButtonPaddingMobile}
            value={searchButtonPadding}
            resetValue={{
              top: "15px",
              left: "15px",
              right: "15px",
              bottom: "15px",
            }}
            onChange={(val) => {
              setProp((props) => {
                props.searchButtonPadding = val;
              });
            }}
            setValueTablet={(val) => {
              setProp((props) => {
                props.searchButtonPaddingTablet = val;
              });
            }}
            setValueMobile={(val) => {
              setProp((props) => {
                props.searchButtonPaddingMobile = val;
              });
            }}
          />
          <BorderInput
            resetValue={undefined}
            value={searchButtonBorder}
            onChange={(val) => {
              setProp((props) => {
                props.searchButtonBorder = val;
              });
            }}
            label={"Border"}
          />
          <BorderRadiusInput
            resetValue={undefined}
            value={searchButtonBorderRadius}
            onChange={(val) => {
              setProp((props) => {
                props.searchButtonBorderRadius = val;
              });
            }}
            label={"Border Radius"}
          />
          <BoxShadowInput
            resetValue={undefined}
            value={searchButtonBoxShadow}
            onChange={(val) => {
              setProp((props) => {
                props.searchButtonBoxShadow = val;
              });
            }}
          />
        </Panel>
        {supports &&
          supports.map((support) => {
            var Component = supportLibrary[support]?.component;

            if (!Component) {
              return;
            }

            return (
              <>
                <Component
                  block={{
                    id,
                    props,
                    dom,
                  }}
                  actions={{
                    setProp,
                  }}
                ></Component>
              </>
            );
          })}
      </>
    );
  }
  const tabs = [
    {
      label: "General",
      key: "general",
      children: GeneralSettings(),
      isUpdated: isUpdatedFromDefault([
        { key: containerWidth },
        { key: containerWidthMobile },
        { key: containerWidthTablet },
        { key: containerHeight },
        { key: containerHeightMobile },
        { key: containerHeightTablet },
        { key: containerMaxWidth, default: "100%" },
        { key: containerMaxHeight },
        { key: gapMobile },
        { key: gapTablet },
        { key: gap },
        { key: placeholderText, default: "Search..." },
        { key: inputWidth },
        { key: inputWidthMobile },
        { key: inputWidthTablet },
        { key: inputHeight },
        { key: inputHeightMobile },
        { key: inputHeightTablet },
        { key: inputMaxWidth },
        { key: inputMaxHeight },
        { key: isSearchButtonVisible, default: true },
        { key: isSearchButtonIcon, default: true },
        { key: iconSize, default: "20px" },
        { key: iconSizeMobile },
        { key: iconSizeTablet },
        { key: icon, default: "search" },
        { key: hasCustomIcon },
        { key: customIcon },
        { key: searchButtonText, default: "Search" },
      ]),
    },
    {
      label: "Style",
      key: "style",
      children: StyleSettings(),
      isUpdated: isUpdatedFromDefault([
        { key: inputColor },
        { key: containerBackground },
        { key: inputPlaceholderColor },
        { key: inputBackgroundColor },
        { key: searchColor, default: "#ffffff" },
        { key: searchBackground, default: "#745dcd" },
      ]),
    },
    {
      label: "Advanced",
      key: "advanced",
      children: AdvancedSettings(),
      isUpdated: isUpdatedFromDefault([
        { key: containerPaddingTablet },
        { key: containerPaddingMobile },
        { key: containerPadding },
        {
          key: containerBorder,
          default: {
            top: {
              width: "1px",
              style: "solid",
              color: "#666666",
            },
            left: {
              width: "1px",
              style: "solid",
              color: "#666666",
            },
            right: {
              width: "1px",
              style: "solid",
              color: "#666666",
            },
            bottom: {
              width: "1px",
              style: "solid",
              color: "#666666",
            },
          },
        },
        { key: containerBorderRadius },
        { key: containerBoxShadow },
        { key: inputPaddingTablet },
        { key: inputPaddingMobile },
        { key: inputPadding },
        { key: inputBorder },
        { key: inputBorderRadius },
        { key: inputBoxShadow },
        { key: searchButtonPaddingTablet },
        { key: searchButtonPaddingMobile },
        {
          key: searchButtonPadding,
          default: {
            top: "15px",
            left: "15px",
            right: "15px",
            bottom: "15px",
          },
        },
        { key: searchButtonBorder },
        { key: searchButtonBorderRadius },
        { key: searchButtonBoxShadow },
      ]),
    },
  ];

  return (
    <div>
      <Tabs
        type="block-setting"
        id={`block-settings-tab-${id}`}
        items={tabs.map((item, i) => {
          return {
            label: item.label,
            key: item.key,
            children: item.children,
          };
        })}
      />
    </div>
  );
};
